import styled from "styled-components";
import { Link } from "react-router-dom";

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.navy600};
  border-radius: 12px;
  overflow: hidden;
  padding: 16px;
`;

export const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const HeaderLine = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderMeta = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ThumbnailWrapper = styled.div`
  background: ${({ theme }) => theme.colors.midnight};
  border-radius: 9px;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:hover .feed-item-play {
    opacity: 1;
  }
`;

export const Thumbnail = styled.img`
  aspect-ratio: 16 / 9;
  display: block;
  object-fit: cover;
  width: 100%;
`;

// Always mounted (not conditional on $active) so the opacity transition has
// something to animate from — the thumbnail underneath stays visible while
// this fades in, masking the black frame the <video> shows before its first
// real frame decodes. Same technique as Homepage/ContentItem's hover preview.
export const VideoOverlay = styled.div<{ $active: boolean }>`
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
  position: absolute;
  transition: opacity 0.4s ease-in-out;
  z-index: 1;
`;

export const MuteToggle = styled.button`
  align-items: center;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  bottom: 10px;
  color: #fff;
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  left: 10px;
  position: absolute;
  transition: background 0.15s ease;
  width: 30px;
  z-index: 2;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

export const StatOverlay = styled.div`
  align-items: flex-end;
  bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 70%;
  position: absolute;
  right: 10px;
  z-index: 2;
`;

export const PlayOverlay = styled.div`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: 0.85;
  position: absolute;
  transition: opacity 0.15s ease;
  z-index: 2;
`;

// display:none can make some browsers deprioritize/skip loading a video
// element entirely, so this stays "visible" at 1x1px and invisible via
// opacity instead — position:absolute keeps it out of the card's layout.
export const PreloadVideo = styled.video`
  height: 1px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  width: 1px;
`;

export const BadgesOverlay = styled.div`
  left: 10px;
  position: absolute;
  top: 10px;
  z-index: 2;
`;

export const CompetitionCard = styled(Link)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.premiumPurpleAlpha50};
  border-radius: 12px;
  display: block;
  overflow: hidden;
  text-decoration: none;

  &:hover {
    box-shadow: 0 12px 30px -18px rgba(0, 0, 0, 0.7);
  }
`;

// The whole card is this box now — banner, scrim, and every piece of copy
// stack as layers inside it instead of copy living in a separate panel below
// a small image. min-height (not aspect-ratio) because the content stack's
// height genuinely varies by state — upcoming has less to show than a
// completed competition with a full winners row — an aspect-ratio box would
// either clip the fuller states or waste space on the sparser ones.
export const CompetitionMedia = styled.div`
  background: ${({ theme }) => theme.colors.midnight};
  min-height: 420px;
  position: relative;
`;

// Background-image div rather than an <img> — matches how the competition
// detail page's own hero (HeroBg) renders its banner, and sizes more
// predictably against a min-height (vs. aspect-ratio) container.
export const CompetitionBanner = styled.div<{ $src?: string }>`
  background: ${({ $src }) =>
    $src ? `url(${$src}) center/cover no-repeat` : "none"};
  inset: 0;
  position: absolute;
`;

// Bottom-anchored gradient, heavier and taller than a simple card scrim since
// the content stack now runs title through winners — same purpose as the
// competition detail page's own HeroScrim, reshaped for a bottom-loaded
// vertical card instead of a wide side-lit hero.
export const CompetitionScrim = styled.div`
  background: linear-gradient(
    to top,
    rgba(6, 10, 14, 0.98) 0%,
    rgba(6, 10, 14, 0.92) 45%,
    rgba(6, 10, 14, 0.55) 75%,
    rgba(6, 10, 14, 0.1) 100%
  );
  inset: 0;
  position: absolute;
`;

type TCompetitionBadgeVariant = "live" | "upcoming" | "ended";

export const CompetitionStatusBadge = styled.span<{
  $variant: TCompetitionBadgeVariant;
}>`
  align-items: center;
  background: ${({ $variant, theme }) =>
    $variant === "live"
      ? theme.colors.turbo
      : $variant === "upcoming"
      ? theme.colors.goldStar
      : "rgba(0, 0, 0, 0.6)"};
  border-radius: 999px;
  color: ${({ $variant, theme }) =>
    $variant === "ended" ? theme.colors.chalk : theme.colors.darkerNavy};
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  left: 16px;
  letter-spacing: 0.05em;
  padding: 6px 12px;
  position: absolute;
  text-transform: uppercase;
  top: 16px;
  z-index: 2;
`;

export const LiveDot = styled.span`
  background: currentColor;
  border-radius: 50%;
  height: 6px;
  width: 6px;
  animation: activity-feed-live-pulse 1.4s ease-in-out infinite;

  @keyframes activity-feed-live-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }
`;

export const CompetitionOverlay = styled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  left: 0;
  padding: 20px;
  position: absolute;
  right: 0;
  z-index: 2;
`;

export const CompetitionTitle = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.08;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);
`;

export const CompetitionByline = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const CompetitionCreatorLink = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 7px;
  padding: 0;

  &:hover span {
    text-decoration: underline;
  }
`;

export const CompetitionCreatorLabel = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12.5px;
  opacity: 0.75;
`;

export const CompetitionCreatorName = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12.5px;
  font-weight: 700;
`;

export const CompetitionOfficialTag = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.turbo};
  display: inline-flex;
  font-size: 12.5px;
  font-weight: 700;
  gap: 5px;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
`;

export const CompetitionMetaDivider = styled.span`
  background: rgba(255, 255, 255, 0.25);
  height: 12px;
  width: 1px;
`;

export const CompetitionMetaCount = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 12.5px;
  font-weight: 600;
  gap: 6px;
  opacity: 0.85;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  b {
    font-weight: 800;
    opacity: 1;
  }
`;

export const CompetitionPrizeBlock = styled.div``;

export const CompetitionPrizeLabel = styled.div`
  color: ${({ theme }) => theme.colors.goldStar};
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const CompetitionPrizeValue = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 2px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
`;

// Frosted glass box, same construction as the detail page's countdown strip
// (CountdownWrapper) — a distinct "widget" rather than another line of text.
// align-self + width keep this hugging its own content — without it, the box
// stretches to CompetitionOverlay's full card width (unlike the detail page,
// where the equivalent widget sits inside a fixed 320px aside column), which
// spread the four digit cells way out since each is flex: 1 of that width.
export const CompetitionCountdownBox = styled.div`
  align-self: flex-start;
  background: rgba(12, 19, 24, 0.6);
  backdrop-filter: blur(10px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  padding: 10px 14px;
  width: 230px;
`;

export const CompetitionCountdownLabel = styled.div`
  color: ${({ theme }) => theme.colors.caution};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const CompetitionCountdownDigits = styled.div`
  align-items: baseline;
  display: flex;
  gap: 4px;
  justify-content: space-between;
  margin-top: 6px;
`;

export const CompetitionCountdownCell = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

export const CompetitionCountdownNum = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
`;

export const CompetitionCountdownUnit = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-top: 4px;
  opacity: 0.7;
  text-transform: uppercase;
`;

export const CompetitionCountdownSep = styled.div`
  color: rgba(255, 255, 255, 0.35);
  font-size: 18px;
  font-weight: 300;
  line-height: 1;
`;

export const CompetitionEndedRow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
  justify-content: flex-start;
  padding: 4px 0;

  svg {
    color: ${({ theme }) => theme.colors.caution};
  }
`;

export const CompetitionChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const CompetitionChip = styled.span`
  align-items: center;
  background: rgba(8, 12, 16, 0.55);
  backdrop-filter: blur(2px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 5px;
  padding: 3px 8px;

  svg {
    color: ${({ theme }) => theme.colors.ice};
    flex-shrink: 0;
    opacity: 0.85;
  }
`;

// Wraps WinnersHeading + WinnersFeatureRow — the extra top padding gives the
// section room to breathe below the rule chips instead of sitting flush
// against them.
export const WinnersSection = styled.div`
  padding-bottom: 14px;
  padding-top: 14px;
`;

export const WinnersHeading = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.goldStar};
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  justify-content: center;
  letter-spacing: 0.1em;
  padding-bottom: 10px;
  text-transform: uppercase;

  svg {
    flex-shrink: 0;
  }
`;

// Podium arrangement: 2nd — 1st — 3rd, 1st place rendered larger via
// $featured and bottom-aligned so its extra height reads as "elevated"
// rather than the row just being uneven.
export const WinnersFeatureRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 18px;
  justify-content: center;
`;

// Not a Link: this renders inside CompetitionCard, which is itself a Link —
// nested <a> tags are invalid HTML and break click behavior, so navigation
// here is handled with useHistory instead.
export const WinnerFeatureItem = styled.div<{ $featured?: boolean }>`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: ${({ $featured }) => ($featured ? 76 : 60)}px;

  &:hover img {
    transform: scale(1.05);
  }
`;

export const WinnerAvatarRing = styled.div<{
  $color?: string;
  $featured?: boolean;
}>`
  border: ${({ $featured }) => ($featured ? 3 : 2)}px solid
    ${({ $color, theme }) => $color ?? theme.colors.chalk};
  border-radius: 50%;
  box-shadow: ${({ $featured }) =>
    $featured ? "0 0 14px rgba(255, 215, 0, 0.3)" : "none"};
  height: ${({ $featured }) => ($featured ? 68 : 48)}px;
  padding: 2px;
  position: relative;
  width: ${({ $featured }) => ($featured ? 68 : 48)}px;
`;

export const WinnerFeatureAvatar = styled.img`
  border-radius: 50%;
  display: block;
  height: 100%;
  object-fit: cover;
  transition: transform 0.15s ease;
  width: 100%;
`;

export const WinnerRankBadge = styled.span<{
  $color?: string;
  $featured?: boolean;
}>`
  align-items: center;
  background: ${({ $color, theme }) => $color ?? theme.colors.chalk};
  border: 2px solid ${({ theme }) => theme.colors.darkerNed};
  border-radius: 50%;
  bottom: -4px;
  color: ${({ theme }) => theme.colors.darkerNavy};
  display: flex;
  font-size: ${({ $featured }) => ($featured ? 10 : 9)}px;
  font-weight: 800;
  height: ${({ $featured }) => ($featured ? 19 : 17)}px;
  justify-content: center;
  position: absolute;
  right: -4px;
  width: ${({ $featured }) => ($featured ? 19 : 17)}px;
`;

export const WinnerFeatureName = styled.span<{ $featured?: boolean }>`
  color: ${({ theme }) => theme.colors.ice};
  font-size: ${({ $featured }) => ($featured ? 12.5 : 11)}px;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const WinnerFeatureScore = styled.span<{
  $color?: string;
  $featured?: boolean;
}>`
  color: ${({ $color, theme }) => $color ?? theme.colors.ice};
  font-size: ${({ $featured }) => ($featured ? 12 : 10.5)}px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  opacity: 0.9;
`;

// Still small/plain next to the Clip and Competition cards' full hero
// treatment — a "by the way, new post" teaser, just with a bit more visual
// interest than flat text. overflow:hidden clips BlogBackgroundAvatar to the
// card's rounded corners.
export const BlogCard = styled(Link)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.turboAlpha50};
  border-radius: 12px;
  display: block;
  overflow: hidden;
  padding: 16px 18px;
  position: relative;
  text-decoration: none;

  &:hover {
    box-shadow: 0 12px 30px -18px rgba(0, 0, 0, 0.7);
  }
`;

// Blog posts have no thumbnail field of their own (see homepage.ts) — the
// author's avatar, blown up and faded into the card via mask-image, is the
// only visual asset available. Sits behind BlogContent (z-index 0 vs. 1).
export const BlogBackgroundAvatar = styled.img`
  -webkit-mask-image: linear-gradient(to left, black 25%, transparent 100%);
  height: 100%;
  mask-image: linear-gradient(to left, black 25%, transparent 100%);
  object-fit: cover;
  opacity: 0.5;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  width: 55%;
  z-index: 0;
`;

export const BlogContent = styled.div`
  max-width: 65%;
  position: relative;
  z-index: 1;
`;

export const BlogTag = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.turbo};
  display: inline-flex;
  font-size: 24px;
  font-weight: 800;
  gap: 9px;
  letter-spacing: 0.03em;
  margin-bottom: 12px;
  text-transform: uppercase;
`;

export const BlogTitle = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ theme }) => theme.colors.ice};
  display: -webkit-box;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.3;
  overflow: hidden;
`;

export const BlogByline = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 12.5px;
  gap: 6px;
  margin-top: 10px;

  > *:not(:last-child)::after {
    content: "\\00b7";
    margin-left: 6px;
  }
`;

export const BlogAuthorGroup = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 5px;
`;

export const BlogAvatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 20px;
  object-fit: cover;
  width: 20px;
`;

export const BlogAuthorName = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 600;
`;
